import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, Lock01, Mail01, ShieldTick } from '@untitledui/icons';
import { toast } from 'react-toastify';
import { AuthContext } from '../App';
import { apiService } from '../services/api';
import { TokenResponse, TokenService } from '../services/tokenService';
import logo from '../assets/logo.jpeg';
import { Button } from './untitled-ui/base/buttons/button';
import { Input } from './untitled-ui/base/input/input';

interface OtpChallengeResponse {
  challengeId: string;
  maskedPhone: string;
  expiresIn: number;
  resendAfter: number;
  attemptsRemaining: number;
}

const Login: React.FC = () => {
  const navigate = useNavigate();
  const { setIsAuthenticated } = useContext(AuthContext);
  const [isLoading, setIsLoading] = useState(false);
  const [credentials, setCredentials] = useState({ username: '', password: '' });
  const [otpChallenge, setOtpChallenge] = useState<OtpChallengeResponse | null>(null);
  const [otp, setOtp] = useState('');
  const [resendCountdown, setResendCountdown] = useState(0);
  const [attemptsRemaining, setAttemptsRemaining] = useState(5);

  useEffect(() => {
    if (resendCountdown <= 0) return;
    const timer = window.setInterval(() => setResendCountdown(value => Math.max(0, value - 1)), 1000);
    return () => window.clearInterval(timer);
  }, [resendCountdown]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setIsLoading(true);
    try {
      const response = await apiService.post<OtpChallengeResponse>('/api/auth/login', credentials);
      if (response.success && response.data) {
        setOtpChallenge(response.data);
        setResendCountdown(response.data.resendAfter);
        setAttemptsRemaining(response.data.attemptsRemaining);
        setOtp('');
        toast.success(`OTP sent to ${response.data.maskedPhone}`);
      } else toast.error(response.message || 'Login failed');
    } catch (error: any) {
      toast.error(error.message || 'Login failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOtpSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!otpChallenge) return;
    setIsLoading(true);
    try {
      const response = await apiService.post<TokenResponse>('/api/auth/verify-otp', { challengeId: otpChallenge.challengeId, otp });
      if (response.success && response.data) {
        TokenService.setTokens(response.data);
        setIsAuthenticated(true);
        navigate('/dashboard', { replace: true });
        toast.success('Welcome back');
      } else {
        if (response.message?.startsWith('Invalid OTP')) setAttemptsRemaining(value => Math.max(0, value - 1));
        toast.error(response.message || 'OTP verification failed');
      }
    } catch (error: any) {
      toast.error(error.message || 'OTP verification failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (!otpChallenge || resendCountdown > 0) return;
    setIsLoading(true);
    try {
      const response = await apiService.post<OtpChallengeResponse>('/api/auth/resend-otp', { challengeId: otpChallenge.challengeId });
      if (response.success && response.data) {
        setOtpChallenge(response.data);
        setResendCountdown(response.data.resendAfter);
        setAttemptsRemaining(response.data.attemptsRemaining);
        setOtp('');
        toast.success('A new OTP was sent');
      } else toast.error(response.message || 'Failed to resend OTP');
    } catch (error: any) {
      toast.error(error.message || 'Failed to resend OTP');
    } finally {
      setIsLoading(false);
    }
  };

  const returnToCredentials = () => {
    setOtpChallenge(null);
    setOtp('');
    setResendCountdown(0);
    setAttemptsRemaining(5);
  };

  return (
    <main className="login-page">
      <section className="login-visual" aria-hidden="true">
        <div className="login-visual-grid"></div>
        <div className="login-visual-content">
          <span className="login-visual-badge"><ShieldTick /> Gym operations, simplified</span>
          <h1>Run your gym with complete clarity.</h1>
          <p>Members, attendance, payments, and training plans — organized in one calm workspace.</p>
          <div className="login-proof-row">
            <span><CheckCircle /> Secure access</span>
            <span><CheckCircle /> Live reporting</span>
          </div>
        </div>
      </section>

      <section className="login-form-side">
        <div className="login-card">
          <div className="login-brand">
            <img src={logo} alt="Cenadz Fitness" />
            <div><strong>Cenadz Fitness</strong><span>Management portal</span></div>
          </div>
          <div className="login-heading">
            <span>{otpChallenge ? 'SECURE VERIFICATION' : 'WELCOME BACK'}</span>
            <h2>{otpChallenge ? 'Check your phone' : 'Log in to your account'}</h2>
            <p>{otpChallenge ? `Enter the six-digit code sent to ${otpChallenge.maskedPhone}.` : 'Enter your credentials to continue to your workspace.'}</p>
          </div>

          {otpChallenge ? (
            <form onSubmit={handleOtpSubmit} className="login-form">
              <Input label="Verification code" icon={ShieldTick} placeholder="000000" value={otp}
                inputMode="numeric" autoComplete="one-time-code" inputClassName="login-otp-input"
                onChange={value => setOtp(value.replace(/\D/g, '').slice(0, 6))} maxLength={6} isRequired autoFocus />
              <p className={`login-attempts ${attemptsRemaining <= 1 ? 'danger' : ''}`}>{attemptsRemaining} verification {attemptsRemaining === 1 ? 'attempt' : 'attempts'} remaining</p>
              <Button className="login-primary-button" size="lg" color="primary" type="submit" isLoading={isLoading} isDisabled={otp.length !== 6 || attemptsRemaining === 0}>
                Verify and log in
              </Button>
              <Button className="login-text-button" size="sm" color="link-gray" onPress={handleResendOtp} isDisabled={isLoading || resendCountdown > 0}>
                {resendCountdown > 0 ? `Resend code in ${resendCountdown}s` : 'Resend verification code'}
              </Button>
              <Button className="login-text-button" size="sm" color="link-gray" onPress={returnToCredentials} isDisabled={isLoading}>Use different credentials</Button>
            </form>
          ) : (
            <form onSubmit={handleSubmit} className="login-form">
              <Input label="Username or email" icon={Mail01} placeholder="Enter your username" value={credentials.username}
                onChange={username => setCredentials(value => ({ ...value, username }))} autoComplete="username" isRequired />
              <Input label="Password" icon={Lock01} type="password" placeholder="Enter your password" value={credentials.password}
                onChange={password => setCredentials(value => ({ ...value, password }))} autoComplete="current-password" isRequired />
              <Button className="login-primary-button" size="lg" color="primary" type="submit" isLoading={isLoading}>Continue</Button>
            </form>
          )}
          <p className="login-footer-copy">Protected with secure one-time verification.</p>
        </div>
      </section>
    </main>
  );
};

export default Login;
